import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { getCurrentCountry } from '@front/common/src/utils/country';

import { useUniverse } from '@front/pro/src/signup/signup-form-context';

import { ModalProvider } from '@front/web-ui/src/components/modal/modal-container-with-backdrop';
import ScrollToTop from '@front/web-ui/src/components/scroll-to-top/scroll-to-top';
import { boxShadow, minWidth } from '@front/web-ui/src/styles/mixins';

import { getRoute } from './routes/routes';
import { SignupFormContextProviderWeb } from './contexts/signup-form-context-provider-web';
import ProfessionalStatus from './screens/professional-status/professional-status';
import AdditionalInformation from './screens/additional-information/additional-information';
import UserInformation from './screens/user-information/user-information';
import RevenueSimulator from './screens/revenue-simulator/revenue-simulator';
import Appointment from './screens/appointment/appointment';
import Congratulations from './screens/congratulations/congratulations';
import NotQualified from './screens/not-qualified/not-qualified';
import { FormProgressBar } from './components/form-progress-bar/form-progress-bar';
import { setUserProperties } from './utils/tracking';
import { useShouldShowRevenueSimulator } from './hooks/use-show-revenue-simulator';

const AppWrapper = styled.div`
  ${minWidth.md`
    min-height: 630px;
  `}
`;
// $isRootRoute is used so that the height of the container fits the content for the simulator in mobile view only.
const AppContainer = styled.div<{ $isRootRoute: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 30px;
  min-height: ${({ $isRootRoute }) => ($isRootRoute ? 'unset' : '630px')};

  ${minWidth.md`
    ${boxShadow.normal}
    max-width: 375px;
  `}
`;

const queryClient = new QueryClient();

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const AppContent = () => {
  const location = useLocation();
  const isRootRoute = location.pathname === getRoute('revenue_simulator');
  const shouldShowRevenueSimulator = useShouldShowRevenueSimulator();
  const utmSource = new URLSearchParams(location.search).get('utm_source') || undefined;
  const { universe } = useUniverse();

  useEffect(() => {
    setUserProperties({
      pro_universe: universe,
      utm_source: utmSource,
      country_code: getCurrentCountry(),
    });
  }, [utmSource, universe]);

  return (
    <AppWrapper>
      <AppContainer $isRootRoute={isRootRoute}>
        <ScrollToTop />
        <FormProgressBar />
        <SentryRoutes>
          <Route path={getRoute('not_qualified')} element={<NotQualified />} />
          <Route path={getRoute('confirmation')} element={<Congratulations />} />
          <Route path={getRoute('appointment')} element={<Appointment />} />
          <Route path={getRoute('pro_status')} element={<ProfessionalStatus />} />
          <Route path={getRoute('additional_information')} element={<AdditionalInformation />} />
          <Route path={getRoute('register')} element={<UserInformation />} />
          <Route
            path={`${getRoute('revenue_simulator')}*`}
            element={
              shouldShowRevenueSimulator ? (
                <RevenueSimulator />
              ) : (
                <Navigate to={{ pathname: getRoute('register'), search: location.search }} replace />
              )
            }
          />
        </SentryRoutes>
      </AppContainer>
    </AppWrapper>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ModalProvider>
      <SignupFormContextProviderWeb>
        <AppContent />
      </SignupFormContextProviderWeb>
    </ModalProvider>
  </QueryClientProvider>
);

export default App;
